import { entityId } from '@open-mercato/shared/modules/dsl'

export const PARTICIPANT_PROFILE_ENTITY_ID = entityId('participants', 'participant_profile')

export const participantTypeValues = [
  'SHIPPER',
  'CARRIER',
  'BROKER',
  'FORWARDER',
  'TERMINAL_OPERATOR',
  'INTERMODAL_OPERATOR',
] as const

export const supportedModeValues = [
  'ROAD',
  'RAIL',
  'SEA',
  'AIR',
] as const

export const supportedSegmentValues = [
  'FTL',
  'LTL',
  'BULK',
  'CONTAINER',
  'INTERMODAL',
  'HAZMAT',
  'OVERSIZED',
  'TEMPERATURE_CONTROLLED',
] as const

export type ParticipantType = (typeof participantTypeValues)[number]
export type SupportedMode = (typeof supportedModeValues)[number]
export type SupportedSegment = (typeof supportedSegmentValues)[number]

export const participantTypeLabels: Record<ParticipantType, string> = {
  SHIPPER: 'Shipper',
  CARRIER: 'Carrier',
  BROKER: 'Broker',
  FORWARDER: 'Forwarder',
  TERMINAL_OPERATOR: 'Terminal operator',
  INTERMODAL_OPERATOR: 'Intermodal operator',
}

export const supportedModeLabels: Record<SupportedMode, string> = {
  ROAD: 'Road',
  RAIL: 'Rail',
  SEA: 'Sea',
  AIR: 'Air',
}

export const supportedSegmentLabels: Record<SupportedSegment, string> = {
  FTL: 'FTL',
  LTL: 'LTL',
  BULK: 'Bulk',
  CONTAINER: 'Container',
  INTERMODAL: 'Intermodal',
  HAZMAT: 'Hazmat',
  OVERSIZED: 'Oversized',
  TEMPERATURE_CONTROLLED: 'Temperature controlled',
}

export function normalizeStringArray<T extends string>(values: readonly T[] | null | undefined): T[] {
  return Array.from(new Set((values ?? []).filter((value): value is T => typeof value === 'string' && value.length > 0)))
}

export function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
}

export function readOptionalString(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export function readOptionalNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

export function formatCustomerEntityLabel(value: string | null | undefined, fallback: string): string {
  return typeof value === 'string' && value.trim().length > 0 ? value : fallback
}

export function formatParticipantTypeLabel(value: string | null | undefined, fallback = 'Participant'): string {
  if (!value) return fallback
  return participantTypeLabels[value as ParticipantType] ?? fallback
}

export function formatSupportedModeLabel(value: string | null | undefined): string {
  if (!value) return 'Unknown'
  return supportedModeLabels[value as SupportedMode] ?? value
}

export function formatSupportedSegmentLabel(value: string | null | undefined): string {
  if (!value) return 'Unknown'
  return supportedSegmentLabels[value as SupportedSegment] ?? value
}
